import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { selectAchInstructions, selectInfo, selectNraIndicator, selectStateCode } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { ReviewPageActions } from 'projects/money-movement-ui/src/app/create-request/actions';
import { amountTypeDict, Constants, frequencyDict, pageInContext, transactionDetailLabels, transactionDirection, transactionTypes, transactionTypesDict } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { selectIsStandingAuthViewPage, selectIsTransactionsViewPage, selectViewFrequency } from 'projects/money-movement-ui/src/app/transaction-view/reducers';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { isAchForm } from '../../../../create-request/models/form/TransactionForm';
import {
  selectAchInfo,
  selectAchInfoToFromAccount,
  selectMoneyLinkType,
  selectStagedStandingAuthid,
  selectStagedTransactionForm,
  selectAchInfoParty
} from '../../../../create-request/reducers';
import { selectViewInContext } from '../../../reducers';
import { FeatureFlagService } from '../../../services/feature-flag-service';
import { selectHasAccessInitiateMoneyMovement } from '../../../../authorize/reducers';
import { DatePipe } from '@angular/common';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-section-ach',
  templateUrl: './transaction-details-section-ach.component.html',
  styleUrls: ['./transaction-details-section-ach.component.scss']
})
export class TransactionDetailsSectionAchComponent implements OnInit, OnDestroy {
  @Input() isInrFromTable: boolean;
  readonly labels = transactionDetailLabels;
  readonly frequency = frequencyDict;
  readonly taxCopyParagraphOne: string = Constants.taxCopyParagraphOne;
  readonly taxCopyParagraphTwo: string = Constants.taxCopyParagraphTwo;
  transactionTypeLabel: string = transactionTypesDict[transactionTypes.ach];
  transferDirection: string;
  withdrawalDate: string;
  completionDate: string;
  withdrawalDateCopy: string = Constants.expectedWithdrawalDateCopy;
  fundsAvailableToTrade: string;
  fundsAvailableToTradeCopy: string = Constants.fundsAvailableToTrade;
  amount: string;
  isIra: boolean;
  isPtrs: boolean;
  contributionReason: string;
  distributionReason: string;
  selectedAuthorization: string;
  nickname: string;
  to1: string;
  to2: string;
  fedTaxPercent: string;
  fedTaxAmount: string;
  stateTaxPercent: string;
  stateTaxAmount: string;
  grossAmount: string;
  netAmount: string;
  trustIncomePercent: string | undefined;
  trustPrincipalPercent: string | undefined;
  trustTaxCategory: string = '';
  trustTaxCode: string | undefined;
  trustPaidFor: string | undefined;
  trustNetTransferAmount: string | undefined;
  trustComment: string | undefined;
  createRequestStep$: Observable<string>;
  frequencyOfOccurrence: string | undefined;
  destroy$: Subject<boolean> = new Subject<boolean>();
  isStandingAuthViewPage: boolean = false;
  isTransactionViewPage: boolean = false;
  endDate: string | null;
  maxTransferAmount: string;
  isThirdParty: boolean = false;
  transactionType: string;
  moneyLinkType: string;
  viewPagePtrsInfoBannerPrimaryText = Constants.viewPagePtrsInfoBannerPrimaryText;
  viewPagePtrsInfoBannerBoldText = Constants.viewPagePtrsInfoBannerBoldText;
  step: string;
  isRecurringSetup: boolean;
  secondCompletionDate: string | null;
  amountType: string;
  accountName: string;
  accountType: string;
  abaNumber: string;
  stateTaxLabel: string = this.labels.stateTax;

  constructor(private store: Store, private datePipe: DatePipe,) {
  }

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(selectStagedTransactionForm)),
      this.store.pipe(select(selectAchInfo))
    ]).pipe(takeUntil(this.destroy$),
    ).subscribe(([form, achInfo]) => {
      if (form?.transactionType && isAchForm(form)) {
        const extraPrompts = form.extraPrompts;
        const transactionDetails = form.transactionDetails;
        this.isRecurringSetup = form.extraPrompts.frequency === 'recurring';
        this.transferDirection = extraPrompts.transferDirection;
        this.withdrawalDate = transactionDetails.withdrawalDate;
        this.completionDate = achInfo.processDate;
        this.fundsAvailableToTrade = transactionDetails.fundsAvailableToTrade;
        this.contributionReason = transactionDetails.contributionReason;
        this.trustIncomePercent = transactionDetails.trustIncomePercent;
        this.trustPrincipalPercent = transactionDetails.trustPrincipalPercent;
        this.trustTaxCategory = transactionDetails.trustTaxCategory ?? '';
        this.trustTaxCode = transactionDetails.trustTaxCode;
        this.trustPaidFor = transactionDetails.trustPaidFor;
        this.trustComment = transactionDetails.trustComment;
        this.fedTaxPercent = achInfo.fedTaxPercent ?? '';
        this.stateTaxPercent = achInfo.stateTaxPercent ?? '';
        this.distributionReason = achInfo.distributionReason ? achInfo.distributionReason :  transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? '';
        this.amountType = achInfo.amountType;
        if (achInfo.amountType === amountTypeDict.net) {
          this.amount = achInfo.transferAmount;
        } else {
          this.amount = achInfo.withdrawalAmount;
        }
        this.frequencyOfOccurrence = this.isRecurringSetup ? achInfo.frequency ? achInfo.frequency : achInfo.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFrequency)!.description : '';
        this.secondCompletionDate = form.transactionDetails?.recurringFrequency === 'SEMIMTH' || (form.transactionDetails?.recurringFrequency === '2 Times/Month') ?
                                    this.datePipe.transform(form.transactionDetails?.secondProcessDate!, 'MM/dd/yyyy') : '';
        this.endDate = this.isRecurringSetup && form.transactionDetails?.endDate ?
                       this.datePipe.transform(form.transactionDetails?.endDate, 'MM/dd/yyyy') : '';


        this.validateDates();
      }
    });

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isIra = info.classifications.isIra;
      this.isPtrs = info.classifications.isPtrs;
    });

    this.store.select(selectStagedStandingAuthid).pipe(takeUntil(this.destroy$)).subscribe(selectedAuthorization => {
      this.selectedAuthorization = selectedAuthorization?.includes('-') ? (selectedAuthorization?.split('-')[1]) : selectedAuthorization;
    });

    this.store
      .select(selectIsStandingAuthViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isStandingAuthViewPage => this.isStandingAuthViewPage = isStandingAuthViewPage);

    this.store
      .select(selectIsTransactionsViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isTransactionViewPage => this.isTransactionViewPage = isTransactionViewPage);

    combineLatest([
      this.store.select(selectAchInstructions),
      this.store.select(selectAchInfoToFromAccount),
      this.store.select(selectViewInContext),
      this.store.select(selectViewFrequency),
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectAchInfoParty)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([instructions, toFromAccount, step, frequency, hasInitReleaseAccess, achInfoParty]) => {
        if((hasInitReleaseAccess && !instructions?.length) || step === pageInContext.viewMsv)
        {
          this.to1 = toFromAccount.financialInstitution;
          this.accountName = `Account name: ${toFromAccount.accountName}`;
          this.to2 = toFromAccount.accountNumber
            ? `Account number: ${toFromAccount.accountNumber}`
            : '';
          this.accountType = `Account type: ${toFromAccount.accountType}`;
          this.abaNumber = `ABA number: ${toFromAccount.abaNumber}`;
          this.step = step;
          if (step === pageInContext.view || step === pageInContext.viewMsv) {
            this.frequencyOfOccurrence = frequency;
          }
          if (achInfoParty === 'Y') {
            this.transactionType = `${this.transactionTypeLabel} - 3rd party`;
          } else {
            this.transactionType = `${this.transactionTypeLabel} - 1st party`;
          }
        }
        else{
        instructions.forEach((instruction) => {
          this.to1 = toFromAccount.financialInstitution;
          this.accountName = `Account name: ${toFromAccount.accountName}`;
          this.to2 = toFromAccount.accountNumber
            ? `Account number: ${toFromAccount.accountNumber}`
            : '';
          this.accountType = `Account type: ${toFromAccount.accountType}`;
          this.abaNumber = `ABA number: ${toFromAccount.abaNumber}`
          this.step = step;
          if (step === pageInContext.view) {
            this.frequencyOfOccurrence = frequency;
          }

          if (instruction.cashieringRqstId === this.selectedAuthorization) {
            this.nickname = instruction.nickname;
            this.isThirdParty = instruction.isThirdParty;
            if (this.isThirdParty) {
              this.transactionType = `${this.transactionTypeLabel} - 3rd party`;
            } else {
              this.transactionType = `${this.transactionTypeLabel} - 1st party`;
            }
          }
        });
      }
      });

    this.store
      .select(selectMoneyLinkType)
      .pipe(takeUntil(this.destroy$),
      filter( _ => this.isStandingAuthViewPage || this.isTransactionViewPage))
      .subscribe(moneyLinkType => {
        this.moneyLinkType = moneyLinkType;
        let lastIndex = this.moneyLinkType.lastIndexOf('-')
        this.transactionType = this.transactionTypeLabel + ' ' + this.moneyLinkType.slice(lastIndex)
    });

    this.store.select(selectAchInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.fedTaxAmount = info.fedTaxAmount?.split(' ')[0];
      this.stateTaxAmount = info.stateTaxAmount?.split(' ')[0];
      this.grossAmount = info.withdrawalAmount;
      this.netAmount = info.transferAmount;
      this.trustNetTransferAmount = info.transferAmount;
      this.maxTransferAmount = info.maxTransferAmount;
    });
    this.createRequestStep$ = this.store.select(selectViewInContext);

    combineLatest([
      this.store.select(selectStateCode),
      this.store.select(selectNraIndicator)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([stateCode, nraIndicator]) => {
      if (stateCode && !nraIndicator) {
        this.stateTaxLabel = `${this.labels.stateTax} (${stateCode})`;
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onEditClicked(): void {
    this.store.dispatch(ReviewPageActions.editClicked());
  }

  //Removes default dates return by CMT when data is not available so that N/A logic in HTML is triggered
  validateDates(): void {
    if (this.completionDate?.match("0001-01-01")) {this.completionDate = ''};
    if (this.withdrawalDate?.match("12-31-9999") || this.withdrawalDate?.match("01/01/0001")) {this.withdrawalDate = ''};
  }

  get pageHeader(): string {
    return this.isStandingAuthViewPage ? 'Standing authorization details' : 'Transaction details';
  }

  get isRecurring() {
    // frequency is not set on the transaction flow so this.frequency is needed to not show the end date on the review page
    return this.frequencyOfOccurrence && this.frequencyOfOccurrence !== 'On-Request';
  }

  get isViewForRegularAch() {
    // moneyLinkType will say 'On-Request' when creating or using a standing authorization
    // this will return true only if it's a regular ACH transaction (not a pending SLOA or recurring transaction)
    // that a user is viewing from transactions table
    return this.isTransactionViewPage && !this.moneyLinkType.includes('On-Request') && !this.isRecurring;
  }

  get isView() {
    return this.isTransactionViewPage;
  }

  get isAchIn() {
    return this.transferDirection === transactionDirection.in.toLowerCase()
  }

  get isViewStep() {
    return (this.step === pageInContext.view || this.step === pageInContext.viewMsv)
  }

  isNotEmptyOrNull(param: any) {
    const params = param?.trim();
    if (params === null || params === undefined || params === 'undefined%' || params === '') {
      return false;
    }
    return true;
  }

}
